'use client'

// TODO: this component, and the rest of the search logic, is due for a major overhaul,
// so ignore all of the horrible code in here for now
import React, { memo, useCallback, useMemo, useState } from 'react'

import * as SelectPrimitive from '@radix-ui/react-select'
import { format } from 'date-fns'
import NextLink from 'next/link'
import {
  HiMiniChevronDown,
  HiOutlineArrowTopRightOnSquare,
  HiOutlineChatBubbleLeftRight,
  HiOutlineMagnifyingGlass,
  HiOutlineMapPin,
  HiOutlineSquare3Stack3D,
} from 'react-icons/hi2'
import { toast } from 'sonner'
import { twMerge } from 'tailwind-merge'

import { useAuthInfo } from '@sweetauth/core'

import { ToggleBookmarkIconButton } from '@/components/bookmarks/ToggleBookmarkIconButton'
import { Badge } from '@/components/common/BadgeV2'
import Button from '@/components/common/Button'
import Link from '@/components/common/Link'
import { OrgPermissionGate } from '@/components/common/PermissionGate'
import { Select, SelectContent, SelectItem } from '@/components/common/Select'
import Spinner from '@/components/common/Spinner'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'
import { AddOpportunityToSalesforceButton } from '@/components/integrations/Salesforce/Buttons'
import { useSearchContext } from '@/components/search/context'
import Highlight from '@/components/search/Highlight'
import { ColorIndicator } from '@/components/tags/ColorIndicator'

import { FRONTEND_URL } from '@/constants/env'

import { inRange, utcStringToDate } from '@/helpers/date'
import { cleanFileName, getFileTypeIcon } from '@/helpers/file'
import {
  labelRestates,
  removeAllMarkdownFormattingExceptCode,
  removeMarkdownFormatting,
} from '@/helpers/string'

import { useSafePush } from '@/hooks/navigation/useSafePush'
import { buildOpportunityUrl } from '@/hooks/opportunity/useOpportunityUrl'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useLatestPursuitSchemaId } from '@/hooks/pursuits/schemas/usePursuitSchemas'
import { useCreatePursuit } from '@/hooks/pursuits/useCreatePursuit'
import { usePursuitStages } from '@/hooks/pursuits/usePursuitStages'

import type { BookmarkableType } from '@/types/bookmark/Bookmark'
import type { UUID } from '@/types/Id'
import type { CreatePursuit, Pursuit } from '@/types/pursuit/Pursuit'
import type { PursuitStage } from '@/types/pursuit/Stage'
import type { FileMatch, SearchOut, Tag } from '@/types/Search'

interface Opportunity {
  opportunity: SearchOut
  canInteract: boolean
  requiredTagNames: string[]
  className?: string
}

interface Cell {
  title: string
  value: string
  className?: string
  titleClassName?: string
  valueClassName?: string
}

interface Match {
  file: FileMatch
}

interface Matches {
  // TODO: create loading/not found state
  isLoading: boolean
  matches: FileMatch[] | undefined
}

const Cell: React.FC<Cell> = memo(
  ({ title, value, className, titleClassName, valueClassName }) => {
    return (
      <div
        className={twMerge(
          'flex flex-col gap-y-1 text-sm w-full',
          className ?? '',
        )}
      >
        <span
          className={twMerge(
            'font-semibold text-xs uppercase text-primary-900 dark:text-primary-100',
            titleClassName ?? '',
          )}
        >
          {title}
        </span>
        <Highlight
          className={twMerge(
            value === 'N/A'
              ? 'text-primary-500 dark:text-primary-400'
              : 'text-primary-700 dark:text-primary-300',
            valueClassName ?? '',
          )}
        >
          {value}
        </Highlight>
      </div>
    )
  },
)

Cell.displayName = 'Opportunity.Cell'

const Match: React.FC<Match> = memo(({ file }) => {
  const { createNewRequest } = useSearchContext()

  const icon = useMemo(() => {
    const extension = file.file_name.split('.').pop()
    return getFileTypeIcon(
      extension ?? 'default',
      'text-primary-900 dark:text-primary-100',
    )
  }, [file.file_name])

  const decodedFileName = cleanFileName(file.file_name)

  const handleClickHighlight = useCallback(
    (value: string) => {
      // strip any punctuation from the beginning and end of the value
      const strippedValue = value.replace(/^[^\w\s]+|[^\w\s]+$/g, '')
      createNewRequest(strippedValue)
    },
    [createNewRequest],
  )

  return (
    <div className="flex flex-col gap-y-1 w-full py-[10px] px-3 rounded-md bg-primary-50 america:bg-primary-50/50 dark:bg-primary-800 border border-primary-200 dark:border-primary-800">
      <NextLink
        href={`/files/${file.file_id}`}
        target="_blank"
        rel="noreferrer"
        className="flex items-center w-fit text-sm font-medium text-primary-900 dark:text-primary-100 gap-x-1 line-clamp-1 transition-opacity duration-100 hover:opacity-70"
      >
        <div>{icon}</div>
        <span className="line-clamp-1">{decodedFileName}</span>
      </NextLink>
      <div className="flex flex-col gap-y-1">
        {file.highlights.map((highlight, index) => {
          return (
            <Highlight
              key={index}
              className="text-primary-700 dark:text-primary-200"
              onClick={handleClickHighlight}
            >
              {highlight}
            </Highlight>
          )
        })}
      </div>
    </div>
  )
})

Match.displayName = 'Opportunity.Match'

const Matches: React.FC<Matches> = memo(({ isLoading, matches }) => {
  if (isLoading || matches === undefined) {
    return (
      <div className="flex items-center gap-x-2 py-[10px] w-full px-3 rounded-md bg-primary-50 america:bg-primary-50/50 dark:bg-primary-800 border border-primary-200 dark:border-primary-800">
        <Spinner className="w-4 h-4" />
        <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
          Loading...
        </span>
      </div>
    )
  }

  if (matches && matches.length === 0) {
    return (
      <div className="flex items-center gap-x-2 py-[10px] w-full px-3 rounded-md bg-primary-50 america:bg-primary-50/50 dark:bg-primary-800 border border-primary-200 dark:border-primary-800">
        <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
          No query matches found.
        </span>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-y-3">
      {matches.map((file, index) => (
        <Match key={index} file={file} />
      ))}
    </div>
  )
})

Matches.displayName = 'Opportunity.Matches'

const Opportunity: React.FC<Opportunity> = ({
  opportunity,
  canInteract,
  className,
  requiredTagNames,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const { user } = useAuthInfo()
  const { mutateAsync: createPursuit } = useCreatePursuit({
    orgId: activeOrgId,
  })
  const { data: latestSchemaId } = useLatestPursuitSchemaId({
    orgId: activeOrgId,
  })
  const { safePush } = useSafePush()
  const {
    createdPursuits,
    isStreaming,
    type,
    addCreatedPursuit,
    createNewRequest,
    handleCreateThread,
  } = useSearchContext()

  // const combinedAugmentedQueries = useMemo(() => {
  //   return [request?.query ?? '', ...augmentedQueries].join(',').trim()
  // }, [augmentedQueries, request?.query])

  // const canFetchFileMatches = useMemo(() => {
  //   // TODO: remove
  //   return false
  //   // TODO: remove this once we have the file matches working for all types
  //   if (type !== 'sled_contract') {
  //     return false
  //   }
  //   return combinedAugmentedQueries.length > 0
  // }, [combinedAugmentedQueries])

  const [creatingThread, setCreatingThread] = useState<boolean>(false)
  const [creatingPursuit, setCreatingPursuit] = useState<boolean>(false)

  const { data: pursuitStages } = usePursuitStages({ orgId: activeOrgId })

  // const { data: matches, isLoading: isLoadingMatches } = useFileMatches({
  //   query: combinedAugmentedQueries,
  //   opportunityId: opportunity.id,
  //   opportunityType: type,
  //   enabled: canFetchFileMatches && showFileMatches,
  // })

  const buttonsDisabled = creatingThread || creatingPursuit

  const activeUntil = useMemo(
    () => new Date(opportunity.active_until),
    [opportunity],
  )
  const postedDate = useMemo(
    // for some reason, SAM.gov started posting everything as midnight UTC
    () =>
      type === 'federal_contract'
        ? utcStringToDate(opportunity.posted_date)
        : new Date(opportunity.posted_date),
    [opportunity, type],
  )

  const responseDueIn = useMemo(() => {
    const now = new Date()
    now.setHours(0, 0, 0, 0)

    const dueDate = new Date(activeUntil)
    dueDate.setHours(0, 0, 0, 0)

    const diff = dueDate.getTime() - now.getTime()
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))

    return days
  }, [activeUntil])

  const validDueDate = inRange(activeUntil)

  const responseDueInStr = useMemo(() => {
    if (!validDueDate) {
      return 'N/A'
    }
    if (responseDueIn < 0) {
      return 'Past Due'
    }
    return `${responseDueIn} day${responseDueIn !== 1 ? 's' : ''}`
  }, [responseDueIn])

  const validPostedDate = inRange(postedDate)

  // Determine opportunity status for badge display
  const opportunityStatus = useMemo(() => {
    if (opportunity.has_award) {
      return 'awarded' as const
    }
    if (validDueDate && responseDueIn < 0) {
      return 'past_due' as const
    }
    if (validDueDate && responseDueIn < 7) {
      return 'due_soon' as const
    }
    if (validDueDate) {
      return 'active' as const
    }
    return 'unknown' as const
  }, [opportunity.has_award, validDueDate, responseDueIn])

  const cleanedSummary = removeMarkdownFormatting(opportunity.ai_summary)

  const pursuitId =
    createdPursuits.get(opportunity.id)?.pursuitId ?? opportunity.pursuit_id

  const pursuitStageId =
    createdPursuits.get(opportunity.id)?.pursuitStageId ??
    opportunity.pursuit_stage_id

  const selectPursuitStage = useCallback(
    (stages: PursuitStage[]) => {
      return stages.find((stage) => stage.id === pursuitStageId)
    },
    [pursuitStageId],
  )

  const { data: pursuitStage } = usePursuitStages({
    orgId: activeOrgId,
    selector: selectPursuitStage,
  })

  const pursuitType = useMemo(() => {
    switch (type) {
      case 'dibbs_contract':
        return 'dibbs_contract_pursuit'
      case 'federal_contract':
        return 'federal_contract_pursuit'
      case 'federal_grant':
        return 'federal_grant_pursuit'
      case 'sbir':
        return 'sbir_pursuit'
      case 'sled_contract':
        return 'sled_contract_pursuit'
      default:
        const _exhaustiveCheck: never = type
        throw new Error(`Unhandled pursuit type: ${_exhaustiveCheck}`)
    }
  }, [type])

  const pursuit: CreatePursuit = {
    title: removeMarkdownFormatting(opportunity.title),
    description: cleanedSummary,
    user_ids: user ? [user.userId] : [],
    due_date: activeUntil,
    search_id: opportunity.search_id ?? undefined,
    estimated_value: null,
    pwin: 0,
    pwin_override: null,
    type: pursuitType,
    organization_id: activeOrgId as UUID,
    extended_schema_id: latestSchemaId ?? null,
    // TODO: add this back when pipelines are supported again (7/27/2024)
    // pipeline_id: '', // TODO: this is hacky
    [pursuitType]: { entity_id: opportunity.id },
  }

  const sortTags = (tags: Array<Tag>): Array<Tag> => {
    return tags.sort((a: Tag, b: Tag) => a.title.localeCompare(b.title))
  }

  const tags = useMemo(() => {
    return sortTags(opportunity.tags.filter((tag) => tag.value.length > 0))
  }, [opportunity.tags])

  const tagMap = useMemo(() => {
    const map = new Map<string, Tag>()
    for (const tag of tags) {
      map.set(tag.title.toLowerCase(), tag)
    }
    return map
  }, [tags])

  const summary = removeAllMarkdownFormattingExceptCode(opportunity.ai_summary)

  const adjustedTags = useMemo(() => {
    return requiredTagNames.map((name) => ({
      title: name,
      value: tagMap.get(name.toLowerCase())?.value ?? 'N/A',
    }))
  }, [requiredTagNames, tagMap])

  const buildDetailHref = useCallback(
    (id: string, searchId?: string | null) => {
      return buildOpportunityUrl(id, type, searchId)
    },
    [type, buildOpportunityUrl],
  )

  const handleFindSimilar = () => {
    const url = `${FRONTEND_URL}${buildDetailHref(opportunity.id)}`
    createNewRequest(url)
  }

  // TODO: add this back when pipelines are supported again (7/27/2024)
  // const handleCreatePursuit = useCallback(async () => {
  //   setAddToPipelineModalIsOpen(true)
  // }, [setAddToPipelineModalIsOpen])

  const handleCreatePursuit = useCallback(
    async (stageId: UUID) => {
      try {
        setCreatingPursuit(true)

        const pursuitData = {
          ...pursuit,
          stage_id: stageId,
        }

        const createdPursuit = await createPursuit(pursuitData)
        toast.success('Successfully created pursuit!', {
          action: {
            label: 'View',
            onClick: () => {
              if (activeOrgId) {
                safePush(`/orgs/${activeOrgId}/pursuits/${createdPursuit.id}`)
              }
            },
          },
        })
        addCreatedPursuit(opportunity.id, createdPursuit.id, stageId)
        return createdPursuit
      } catch (err) {
        // error notification handled in createPursuit
      } finally {
        setCreatingPursuit(false)
      }
    },
    [activeOrgId, pursuit, createPursuit, pursuitStages],
  )

  const handlePursuitStageChange = useCallback(
    async (stageId: UUID) => {
      if (pursuitId) {
        // TODO: update pursuit stage
      } else {
        handleCreatePursuit(stageId)
      }
    },
    [handleCreatePursuit],
  )

  const handleAddToSalesforceSuccess = useCallback(
    (pursuit: Pursuit) => {
      addCreatedPursuit(opportunity.id, pursuit.id, pursuit.stage.id)
    },
    [opportunity.id, addCreatedPursuit],
  )

  const handleViewPursuit = useCallback(() => {
    if (activeOrgId !== '') {
      if (!!pursuitId) {
        safePush(`/orgs/${activeOrgId}/pursuits/${pursuitId}`)
      }
    }
  }, [activeOrgId, pursuitId, safePush])

  const handleCreateChat = useCallback(async () => {
    setCreatingThread(true)
    await handleCreateThread(opportunity)
    setCreatingThread(false)
  }, [opportunity, handleCreateThread])

  const handleClickHighlight = useCallback(
    (value: string) => {
      // strip any punctuation from the beginning and end of the value
      const strippedValue = value.replace(/^[^\w\s]+|[^\w\s]+$/g, '')
      createNewRequest(strippedValue)
    },
    [createNewRequest],
  )

  // const toggleFileSource = useCallback(() => {
  //   // TODO: this is hacky, but it works for now, need to make it not rely on
  //   // virtualizer
  //   if (virtualizer) {
  //     // disable auto scroll when we expand the file matches to prevent it from
  //     // shifting the entire page
  //     virtualizer.shouldAdjustScrollPositionOnItemSizeChange = () => false
  //   }
  //   setShowFileMatches(!showFileMatches)
  //   if (virtualizer) {
  //     // re-enable auto scroll when we collapse the file matches
  //     setTimeout(() => {
  //       virtualizer.shouldAdjustScrollPositionOnItemSizeChange = undefined
  //     }, 0)
  //   }
  // }, [showFileMatches, setShowFileMatches, virtualizer])

  // Extract specific tags for the sidebar
  const buyerTag = tagMap.get('buyer')
  const locationTag = tagMap.get('location')
  // Use the flat Location tag: it carries the most specific place ("City of Austin, TX",
  // "Alabama"). We deliberately skip the backend Jurisdiction tag — it's built from index 0 of the
  // misaligned jurisdiction arrays (which collapses local opportunities to their state) and is
  // dropped entirely for state-level (no prefix). Skip the result when it only repeats the buyer
  // (a state-fallback buyer equals its state location).
  const sledLocationTag = locationTag
  // The buyer and the flat location often name the same place: a clean buyer ("Plainfield, Town
  // of") and a location that restates it plus the state ("Plainfield, Town of, CT"), or the civil
  // division reordered ("County of Amherst" vs "Amherst County, VA"). When they do, keep the
  // location (it carries the pin and usually the state) and drop the redundant buyer.
  let showSledBuyer = !!buyerTag
  const sledLocationLabel = sledLocationTag ? sledLocationTag.value : null
  if (
    buyerTag &&
    sledLocationTag &&
    labelRestates(buyerTag.value, sledLocationTag.value)
  ) {
    showSledBuyer = false
  }
  const naicsTag = tagMap.get('naics')
  const noticeTypeTag = tagMap.get('notice type')
  const pscTag = tagMap.get('psc')
  const setAsideTag = tagMap.get('set aside')
  // Additional tags for other opportunity types
  const agencyTag = tagMap.get('agency')
  const awardRangeTag = tagMap.get('award range')
  const eligibleApplicantsTag = tagMap.get('eligible applicants')
  const fundingTypeTag = tagMap.get('funding type')
  const branchTag = tagMap.get('branch')
  const programTag = tagMap.get('program')
  const phaseTag = tagMap.get('phase')
  const typeTag = tagMap.get('type')
  const categoryTag = tagMap.get('category')

  return (
    <div
      className={twMerge(
        'group relative flex flex-col md:flex-row w-full rounded-lg border border-primary-200 dark:border-primary-700 bg-white dark:bg-primary-900 overflow-hidden',
        // Status-based left border accent
        opportunityStatus === 'awarded' &&
          'border-l-2 border-l-emerald-400/70 dark:border-l-emerald-500/60',
        opportunityStatus === 'past_due' &&
          'border-l-2 border-l-red-400/70 dark:border-l-red-500/60',
        opportunityStatus === 'due_soon' &&
          'border-l-2 border-l-amber-400/70 dark:border-l-amber-500/60',
        opportunityStatus === 'active' &&
          'border-l-2 border-l-blue-400/70 dark:border-l-blue-500/60',
        opportunityStatus === 'unknown' &&
          'border-l-2 border-l-primary-300/70 dark:border-l-primary-600/60',
        className ?? '',
      )}
    >
      {/* Left Column: Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header: Badge + ID + Buyer/Agency + Location */}
        <div className="flex items-center flex-wrap gap-x-2 gap-y-1 px-4 pt-4 pb-2 text-[13px]">
          {opportunityStatus === 'awarded' && (
            <Badge variant="positive" className="text-xs py-0.5 px-2">
              Awarded
            </Badge>
          )}
          {opportunityStatus === 'past_due' && (
            <Badge variant="destructive" className="text-xs py-0.5 px-2">
              Past Due
            </Badge>
          )}
          {opportunityStatus === 'due_soon' && (
            <Badge
              variant="outline"
              className="text-xs py-0.5 px-2 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700"
            >
              {responseDueIn}d left
            </Badge>
          )}
          {opportunityStatus === 'active' && (
            <Badge variant="blue" className="text-xs py-0.5 px-2">
              {responseDueIn}d left
            </Badge>
          )}
          {opportunityStatus === 'unknown' && (
            <Badge variant="outline" className="text-xs py-0.5 px-2">
              Unknown
            </Badge>
          )}
          {!!opportunity.opportunity_id && (
            <span className="font-mono text-primary-400 dark:text-primary-500">
              {opportunity.opportunity_id}
            </span>
          )}
          {/* Federal Contract: Buyer + Location */}
          {type === 'federal_contract' && buyerTag && (
            <>
              <span className="text-primary-400 dark:text-primary-600">·</span>
              <span className="text-primary-600 dark:text-primary-300">
                {buyerTag.value}
              </span>
              {locationTag && (
                <>
                  <span className="text-primary-400 dark:text-primary-600">
                    ·
                  </span>
                  <span className="flex items-center gap-1 text-primary-500 dark:text-primary-400">
                    <HiOutlineMapPin className="w-3 h-3" />
                    {locationTag.value}
                  </span>
                </>
              )}
            </>
          )}
          {/* SLED: buyer + location, de-duplicated when they name the same place. */}
          {type === 'sled_contract' && (showSledBuyer || sledLocationLabel) && (
            <>
              <span className="text-primary-400 dark:text-primary-600">·</span>
              {showSledBuyer && buyerTag && (
                <span className="text-primary-600 dark:text-primary-300">
                  {buyerTag.value}
                </span>
              )}
              {sledLocationLabel && (
                <>
                  {showSledBuyer && (
                    <span className="text-primary-400 dark:text-primary-600">
                      ·
                    </span>
                  )}
                  <span className="flex items-center gap-1 text-primary-500 dark:text-primary-400">
                    <HiOutlineMapPin className="w-3 h-3" />
                    {sledLocationLabel}
                  </span>
                </>
              )}
            </>
          )}
          {/* Federal Grant: Agency */}
          {type === 'federal_grant' && agencyTag && (
            <>
              <span className="text-primary-400 dark:text-primary-600">·</span>
              <span className="text-primary-600 dark:text-primary-300">
                {agencyTag.value}
              </span>
            </>
          )}
          {/* SBIR: Agency + Branch */}
          {type === 'sbir' && agencyTag && (
            <>
              <span className="text-primary-400 dark:text-primary-600">·</span>
              <span className="text-primary-600 dark:text-primary-300">
                {agencyTag.value}
              </span>
              {branchTag && (
                <>
                  <span className="text-primary-400 dark:text-primary-600">
                    ·
                  </span>
                  <span className="text-primary-500 dark:text-primary-400">
                    {branchTag.value}
                  </span>
                </>
              )}
            </>
          )}
        </div>

        {/* Title */}
        <div className="px-4">
          <Link
            href={buildDetailHref(opportunity.id, opportunity.search_id)}
            className="block font-semibold text-primary-900 dark:text-primary-100 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
            target="_blank"
            rel="noreferrer"
          >
            <Highlight className="text-base md:text-lg leading-snug line-clamp-2 [word-break:keep-all]">
              {opportunity.title.length === 0
                ? 'Untitled Opportunity'
                : opportunity.title}
            </Highlight>
          </Link>
        </div>

        {/* Description */}
        <div className="px-4 pt-2 flex-1">
          <div className="hidden md:block">
            <Highlight
              className="text-sm text-primary-600 dark:text-primary-300 leading-relaxed line-clamp-3"
              clickClassName={twMerge(
                isStreaming ? 'cursor-wait' : 'cursor-pointer',
              )}
              onClick={handleClickHighlight}
            >
              {summary}
            </Highlight>
          </div>
          <div className="md:hidden">
            <span className="text-xs text-primary-600 dark:text-primary-300 leading-5 line-clamp-3">
              {cleanedSummary}
            </span>
          </div>
        </div>

        {/* Actions */}
        {canInteract && (
          <div className="flex items-center justify-between gap-2 px-4 py-3 mt-2 border-t border-primary-100 dark:border-primary-800 overflow-x-auto scrollbar-none">
            <div className="flex items-center gap-2">
              <OrgPermissionGate permission="write" fallback="hide" asChild>
                <Button
                  variant="primary-compact"
                  onClick={handleCreateChat}
                  disabled={buttonsDisabled}
                  isLoading={creatingThread}
                  className="flex-shrink-0"
                >
                  <HiOutlineChatBubbleLeftRight className="flex-shrink-0 w-4 h-4" />
                  <span className="text-xs md:text-sm font-medium">Ask AI</span>
                </Button>
              </OrgPermissionGate>

              {pursuitStage ? (
                <Button
                  variant="outline-compact"
                  disabled={buttonsDisabled}
                  onClick={handleViewPursuit}
                  className="flex-shrink-0"
                >
                  <ColorIndicator color={pursuitStage.color} />
                  <span className="text-xs md:text-sm hidden md:inline">
                    {pursuitStage.name}
                  </span>
                  <HiOutlineArrowTopRightOnSquare
                    size={12}
                    className="text-primary-400"
                  />
                </Button>
              ) : (
                <OrgPermissionGate permission="write" fallback="hide">
                  <Select
                    value={opportunity.pursuit_stage_id || ''}
                    onValueChange={handlePursuitStageChange}
                  >
                    <SelectPrimitive.Trigger asChild>
                      <Button
                        variant="outline-compact"
                        disabled={buttonsDisabled}
                        className="gap-1 flex-shrink-0"
                      >
                        {creatingPursuit ? (
                          <Spinner className="w-3 h-3 fill-white america:!fill-white !text-accent-600 america:!text-red-600" />
                        ) : (
                          <HiOutlineSquare3Stack3D size={14} />
                        )}
                        <span className="text-xs md:text-sm">
                          {creatingPursuit ? '...' : 'Pursue'}
                        </span>
                        {!creatingPursuit && <HiMiniChevronDown size={14} />}
                      </Button>
                    </SelectPrimitive.Trigger>
                    <SelectContent>
                      {pursuitStages?.map((stage) => (
                        <SelectItem key={stage.id} value={stage.id}>
                          <div className="flex items-center gap-2">
                            <ColorIndicator color={stage.color} />
                            <span>{stage.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </OrgPermissionGate>
              )}

              <OrgPermissionGate permission="write" fallback="hide">
                <AddOpportunityToSalesforceButton
                  req={pursuit}
                  pursuitId={pursuitId ?? undefined}
                  onSuccess={handleAddToSalesforceSuccess}
                />
              </OrgPermissionGate>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline-compact"
                    onClick={handleFindSimilar}
                    className="h-7 w-7 shadow-sm"
                  >
                    <HiOutlineMagnifyingGlass
                      size={16}
                      className="flex-shrink-0"
                    />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Find similar opportunities</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <ToggleBookmarkIconButton
              bookmarkableType={type as BookmarkableType}
              objectId={opportunity.id}
              iconSize={16}
              className="h-7 w-7 shadow-sm bg-white dark:bg-primary-900 border border-primary-300 dark:border-primary-700 rounded-md"
            />
          </div>
        )}
      </div>

      {/* Right Column: Metadata Sidebar */}
      <div className="flex-shrink-0 md:w-56 bg-primary-50 dark:bg-primary-800/50 border-t md:border-t-0 md:border-l border-primary-200 dark:border-primary-700 p-4">
        <div className="flex flex-col gap-2.5 text-[13px]">
          {/* Posted date */}
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-primary-500 dark:text-primary-400">
              Posted
            </span>
            <span className="text-primary-900 dark:text-primary-100 tabular-nums">
              {validPostedDate ? format(postedDate, 'MMM d, yyyy') : '—'}
            </span>
          </div>

          {/* Due date with badge */}
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-primary-500 dark:text-primary-400">
              Due Date
            </span>
            <div className="flex items-center gap-2">
              <span className="text-primary-900 dark:text-primary-100 tabular-nums">
                {validDueDate ? format(activeUntil, 'MMM d, yyyy') : '—'}
              </span>
              {validDueDate && responseDueIn >= 0 && responseDueIn < 7 && (
                <Badge
                  variant="outline"
                  className="text-xs py-0.5 px-2 font-semibold bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-500 dark:border-amber-700"
                >
                  {responseDueIn}d
                </Badge>
              )}
              {validDueDate && responseDueIn >= 7 && (
                <Badge
                  variant="outline"
                  className="text-xs py-0.5 px-2 font-medium border-primary-300 dark:border-primary-600"
                >
                  {responseDueIn}d
                </Badge>
              )}
            </div>
          </div>

          {/* Award Value - shown when present */}
          {opportunity.has_award && (
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
                Award Value
              </span>
              <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                {opportunity.award_amount
                  ? `$${new Intl.NumberFormat('en-US', {
                      notation: 'compact',
                      maximumFractionDigits: 1,
                    }).format(opportunity.award_amount)}`
                  : '—'}
              </span>
            </div>
          )}

          {/* Type-specific metadata */}
          {type === 'federal_contract' && (
            <>
              {noticeTypeTag && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-primary-500 dark:text-primary-400">
                    Type
                  </span>
                  <span className="text-primary-900 dark:text-primary-100">
                    {noticeTypeTag.value || 'N/A'}
                  </span>
                </div>
              )}
              {setAsideTag && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-primary-500 dark:text-primary-400">
                    Set Aside
                  </span>
                  <span
                    className={twMerge(
                      setAsideTag.value === 'N/A'
                        ? 'text-primary-400 dark:text-primary-500'
                        : 'text-primary-900 dark:text-primary-100',
                    )}
                  >
                    {setAsideTag.value || 'N/A'}
                  </span>
                </div>
              )}
              {/* NAICS / PSC side by side */}
              <div className="flex gap-4">
                {naicsTag && (
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-primary-500 dark:text-primary-400">
                      NAICS
                    </span>
                    <span className="flex items-center gap-1.5 text-primary-900 dark:text-primary-100">
                      {naicsTag.value || 'N/A'}
                    </span>
                  </div>
                )}
                {pscTag && (
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-primary-500 dark:text-primary-400">
                      PSC
                    </span>
                    <span className="flex items-center gap-1.5 text-primary-900 dark:text-primary-100">
                      {pscTag.value || 'N/A'}
                    </span>
                  </div>
                )}
              </div>
            </>
          )}

          {type === 'federal_grant' && (
            <>
              {awardRangeTag && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-primary-500 dark:text-primary-400">
                    Award Range
                  </span>
                  <span className="text-primary-900 dark:text-primary-100 font-medium">
                    {awardRangeTag.value || 'N/A'}
                  </span>
                </div>
              )}
              {eligibleApplicantsTag && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-primary-500 dark:text-primary-400">
                    Eligible Applicants
                  </span>
                  <span className="text-primary-900 dark:text-primary-100 line-clamp-2">
                    {eligibleApplicantsTag.value || 'N/A'}
                  </span>
                </div>
              )}
              {fundingTypeTag && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-primary-500 dark:text-primary-400">
                    Funding Type
                  </span>
                  <span className="text-primary-900 dark:text-primary-100">
                    {fundingTypeTag.value || 'N/A'}
                  </span>
                </div>
              )}
            </>
          )}

          {type === 'sbir' && (
            <div className="flex gap-4">
              {programTag && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-primary-500 dark:text-primary-400">
                    Program
                  </span>
                  <span className="text-primary-900 dark:text-primary-100">
                    {programTag.value || 'N/A'}
                  </span>
                </div>
              )}
              {phaseTag && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-primary-500 dark:text-primary-400">
                    Phase
                  </span>
                  <span className="text-primary-900 dark:text-primary-100">
                    {phaseTag.value || 'N/A'}
                  </span>
                </div>
              )}
            </div>
          )}

          {type === 'sled_contract' && (
            <>
              {categoryTag && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-primary-500 dark:text-primary-400">
                    Category
                  </span>
                  <span className="text-primary-900 dark:text-primary-100 line-clamp-2">
                    {categoryTag.value || 'N/A'}
                  </span>
                </div>
              )}
              {typeTag && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-primary-500 dark:text-primary-400">
                    Type
                  </span>
                  <span className="text-primary-900 dark:text-primary-100">
                    {typeTag.value || 'N/A'}
                  </span>
                </div>
              )}
            </>
          )}

          {type === 'dibbs_contract' && setAsideTag && (
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-primary-500 dark:text-primary-400">
                Set Aside
              </span>
              <span
                className={twMerge(
                  setAsideTag.value === 'N/A' || setAsideTag.value === 'None'
                    ? 'text-primary-400 dark:text-primary-500'
                    : 'text-primary-900 dark:text-primary-100',
                )}
              >
                {setAsideTag.value || 'N/A'}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default memo(Opportunity, (prev, next) => {
  return prev.opportunity.id === next.opportunity.id
})
